import { useState, useEffect } from "react";
import DataTable from "@/components/organisms/DataTable";
import { ColumnDef } from "@tanstack/react-table";
import ColumnHeader from "@/components/atoms/ColumnHeader";
import Ticket from "@/types/Ticket";
import Pagination from "@/components/atoms/Pagination";
import axios from "axios";

type Props = {
	account_id: string;
	ticket_status_id: string;
}

const TicketTable = ({account_id, ticket_status_id} : Props) => {
	const [tickets, setTickets] = useState<Ticket[]>([]);
	const [pagination, setPagination] = useState({ page: 1, per_page: 10 });
	const [totalPages, setTotalPages] = useState<number>(0);
	const [totalCount, setTotalCount] = useState<number>(0);
	const [isPaginationUpdatedInternally, setIsPaginationUpdatedInternally] = useState(false);


	const columns: ColumnDef<Ticket>[] = [
		{
			id: "displayid",
			accessorKey: "display_id",
			header: ({ column }) => <ColumnHeader column={column}>ID</ColumnHeader>,
		},
		{
			id: "subject",
			accessorKey: "subject",
			header: ({ column }) => <ColumnHeader column={column}>Subject</ColumnHeader>,
		},
		{
			id: "ticketStatus",
			accessorKey: "ticket_status.name",
			header: ({ column }) => <ColumnHeader column={column}>Ticket Status</ColumnHeader>,
		},
		{
			id: "internalOwner",
			accessorKey: "internal_owner.name",
			header: ({ column }) => <ColumnHeader column={column}>Internal Owner</ColumnHeader>,
			cell: ({ row }) => (
        <div className="flex items-center space-x-2">
          {row.original.internal_owner?.avatar_url && (
            <img
              src={row.original.internal_owner.avatar_url}
              alt={row.original.internal_owner.name || "User Avatar"}
              className="w-8 h-8 rounded-full dark:bg-darkTextColor"
              loading="lazy"
            />
          )}
          <span>{row.original.internal_owner?.name || ""}</span>
        </div>
      ),
		},
		{
			id: "function",
			accessorKey: "function.name",
			header: ({ column }) => <ColumnHeader column={column}>Function</ColumnHeader>,
		},
		{
			id: "department",
			accessorKey: "department.name",
			header: ({ column }) => <ColumnHeader column={column}>Department</ColumnHeader>,
		},

		{
			id: "due_date",
			accessorKey: "due_date",
			header: ({ column }) => <ColumnHeader column={column}>Due Date</ColumnHeader>,
		},



	];

	const handlePageChange = (page: number) => {
		setPagination(prev => ({ ...prev, page }));
	};


	useEffect(() => {
		const fetchTickets = async () => {
			const response = await axios.get('/tickets/listing', {
				params: {
					account_ids: [account_id],
					ticket_status_ids:[ticket_status_id],
					...pagination
				}
			});

			setTickets(response.data.tickets);
			setTotalCount(response.data.total_count);
			setTotalPages(response.data.total_pages);

			if (response.data.current_page !== pagination.page) {
				setPagination(prev => ({ ...prev, page: response.data.current_page }));
				setIsPaginationUpdatedInternally(true);
			} else {
				setIsPaginationUpdatedInternally(false);
			}

		};

		if (!isPaginationUpdatedInternally) {
			fetchTickets();
		}

		return () => {
			setIsPaginationUpdatedInternally(false);
		};
	}, [pagination, account_id]);

	return (
		<>
			<DataTable
				columns={columns}
				data={tickets}
				rowUrl={(row) => `/tickets/${row.id}`}
			/>
			<Pagination
				currentPage={pagination.page}
				totalPages={totalPages}
				totalCount={totalCount}
				perPage={pagination.per_page}
				onPageChange={handlePageChange}
			/>
		</>
	);
};

export default TicketTable;
